import { type OrderLine } from '@/features/shop/services/Orders/types'
import { PRODUCT_TYPES, getProductType } from '@/features/shared/utils/normalizeProduct'
import { formatPrice } from '@/features/shared/utils/formatter'
import { SPECIAL_REQUEST_PRODUCT_COST } from '@/features/shop/components/Cart/constants'

export type ProductForPriceFormatting = {
  isCustomProduct: boolean
  onSale: boolean
  totalPrice: number
  totalSalePrice: number
  quantity: number
  price: number
  salePrice: number
  type: string
  hasCustomLabel: boolean
  promotionSubtotal?: number
  originalSubtotal: number
}
/**
 * Returns the numeric subtotal for a single product line (unit price × quantity),
 * accounting for sale price, custom products, and promotions. Mirrors the
 * non-orderLine pricing logic used by `getFormattedPrices`.
 */
export const getProductSubtotal = (product: ProductForPriceFormatting): number => {
  const { isCustomProduct, onSale, totalPrice, totalSalePrice, quantity, promotionSubtotal } =
    product

  if (promotionSubtotal !== undefined) return promotionSubtotal

  if (isCustomProduct) return quantity ? SPECIAL_REQUEST_PRODUCT_COST * quantity : 0

  const unitPrice = onSale ? totalSalePrice : totalPrice || 0

  return quantity ? unitPrice * quantity : 0
}

export const getFormattedPrices = (
  product: ProductForPriceFormatting,
  orderLine: OrderLine<ProductForPriceFormatting> | undefined,
) => {
  const {
    isCustomProduct,
    onSale,
    totalPrice,
    totalSalePrice,
    quantity,
    price,
    salePrice,
    promotionSubtotal,
    originalSubtotal,
  } = product

  const customByWeightProduct = getProductType(product) === PRODUCT_TYPES.CUSTOM_BY_WEIGHT

  let preformattedTotalPrice = onSale ? totalSalePrice : totalPrice || 0
  let originalPrice = customByWeightProduct ? totalPrice : price

  if (isCustomProduct) {
    preformattedTotalPrice = SPECIAL_REQUEST_PRODUCT_COST
    originalPrice = SPECIAL_REQUEST_PRODUCT_COST
  }

  let formattedTotalPrice = quantity ? formatPrice(preformattedTotalPrice * quantity) : '$0.00'
  let formattedTotalOriginalPrice = quantity ? formatPrice(originalPrice * quantity) : '$0.00'

  if (promotionSubtotal !== undefined) {
    formattedTotalOriginalPrice = formatPrice(originalSubtotal)
    formattedTotalPrice = formatPrice(promotionSubtotal)
  }

  if (orderLine) {
    const { actual_product, actual_total, actual_price } = orderLine

    let preformattedPrice = onSale ? Number(salePrice) : Number(price)

    // Some products do not send back an actual_price (custom products & alcohol)
    // so we calculate the price by dividing the actual_total by the quantity.
    // I kept the old logic in place as a fallback in case a product doesn't send
    // back either an actual_price or actual_total.
    if (actual_total) {
      preformattedPrice = quantity ? Number(actual_total) / quantity : 0
    } else if (actual_price) {
      preformattedPrice = Number(actual_price)
    }

    formattedTotalPrice = quantity ? formatPrice(preformattedPrice * quantity) : '$0.00'

    originalPrice = customByWeightProduct && !actual_total ? product.totalPrice : product.price
    formattedTotalOriginalPrice = quantity ? formatPrice(originalPrice * quantity) : '$0.00'

    // When an order is still in progress, Shipt will display an estimated price for customByWeight products per
    // unit label. E.g. The UI shows `2 Turkeys * $13.09`. Total price is the *estimated* price per whole Turkey.
    // Once an order is shopped (an `actual_product` key exists on the order line), the backend will return the
    // `actual_quantity` in terms of unit weight. Now Shipt will display the actual quantity (in terms of weight)
    // and price. E.g. the UI now shows `25.28 lbs × $1.19` if the customer actually received 25.28 lbs of Turkey.
    if (customByWeightProduct) {
      formattedTotalPrice = quantity
        ? formatPrice((actual_product ? preformattedPrice : preformattedTotalPrice) * quantity)
        : '$0.00'
    }
  }

  return {
    /** The total price of the product (unit price × quantity). If the product is on sale, this will be the sale price. */
    formattedTotalPrice,
    /** The total original price of the product (unit price × quantity). This is before discounts are applied. */
    formattedTotalOriginalPrice,
  }
}
